import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { isEmpty } from 'lodash-es';
import { List, ListItem } from 'chaoskit/src/components';

import { Data, Link } from '../components';
import ChaosEngineeringLayout from '../layouts/ChaosEngineeringLayout';
import ChaosEngineeringHero from '../partials/chaos-engineering/Hero';
import { getArticleType } from '../../utils/helpers';

const BottomArticles = ({ articles }) => {
  // Create bucket we'll use to grab an article from each bucket
  const matchedArticles = [];

  for (let i = 0; i < articles.length; i += 1) {
    if (articles[i].tutorial) {
      matchedArticles.push(articles[i].tutorial);
    } else if (articles[i].post) {
      matchedArticles.push(articles[i].post);
    } else if (articles[i].chaosEngineering) {
      matchedArticles.push(articles[i].chaosEngineering);
    }
  }

  if (!matchedArticles.length) return null;

  return matchedArticles.map(node => {
    let type;

    switch (getArticleType(node)) {
      case 'blog':
        type = 'Blog Post';
        break;
      case 'tutorial':
        type = 'Community Tutorial';
        break;
      default:
        type = 'Chaos Engineering Article';
    }

    return (
      <li key={node.fileAbsolutePath}>
        <article className="chaosEngineering__bottomArticle">
          <Link
            to={node.fields.fullUrl}
            title={
              node.frontmatter.titleSub
                ? `${node.frontmatter.title}: ${node.frontmatter.titleSub}`
                : node.frontmatter.title
            }
            className="chaosEngineering__bottomArticle-link"
          />
          <div className="u-textHeading u-textSpace u-textUppercase u-textPrimary">
            {type}
          </div>
          <h4 className="u-mt--regular">{node.frontmatter.title}</h4>
          <div className="chaosEngineering__bottomArticle-excerpt">
            <Data markdown>{node.frontmatter.teaser || node.excerpt}</Data>
          </div>
        </article>
      </li>
    );
  });
};

BottomArticles.propTypes = {
  articles: PropTypes.array,
};

const ChaosEngineering = ({
  data: {
    pageData: { childDataYaml: pageData },
    stageArticles,
  },
}) => (
  <ChaosEngineeringLayout>
    <Helmet
      title={pageData.title}
      meta={[
        { name: 'description', content: pageData.description },
        {
          property: 'og:image',
          content: pageData.socialMediaImage,
        },
      ]}
    />
    <ChaosEngineeringHero
      compact={false}
      className="is-index"
      style={{ backgroundImage: `url(${pageData.heroImage})` }}
    >
      <div className="row">
        <div className="column-6@medium">
          <h1 className="hero-title">
            <Data>{pageData.hero.heading}</Data>
          </h1>
          <div className="hero-titleSub">
            <Data markdown>{pageData.hero.blurb}</Data>
          </div>
        </div>
      </div>
      <img
        className="chaosEngineering__article-hero-image u-hiddenUp@medium"
        src={pageData.heroImage}
        alt=""
      />
    </ChaosEngineeringHero>
    <section className="u-contrast section chaosEngineering__index-body chaosEngineering--links u-bgChaosEngineering">
      <div className="container">
        <div className="section-titleWrapper">
          <h3 className="section-title">
            <Data>{pageData.intro.heading}</Data>
          </h3>
        </div>
        <Data markdown>{pageData.intro.blurb}</Data>
        <div className="u-mt--large">
          <div className="container container--small u-textSmall u-textPrimary">
            <Data markdown>{pageData.intro.blurbMeta}</Data>
          </div>
        </div>
      </div>
    </section>
    <section className="u-contrast section section--large chaosEngineering--links chaosEngineering__index-bodyAlt">
      <div className="container">
        <div className="blockGrid blockGrid-2@medium blockGrid--largeGutter">
          <div className="column-6@medium">
            <h3 className="u-mb--large">{pageData.what.heading}</h3>
            <Data markdown>{pageData.what.blurb}</Data>
          </div>
          <div className="column-6@medium">
            <h3 className="u-mb--large">{pageData.who.heading}</h3>
            <Data markdown>{pageData.who.blurb}</Data>
          </div>
          <div className="column-6@medium">
            <h3 className="u-mb--large">{pageData.why.heading}</h3>
            <Data markdown>{pageData.why.blurb}</Data>
          </div>
        </div>
      </div>
    </section>
    <section className="u-contrast section section--large u-bgChaosEngineering chaosEngineering__index-body chaosEngineering--links">
      <div className="container">
        <Data markdown>{pageData.body}</Data>
      </div>
    </section>
    <section className="section section--large u-bgPanel">
      <div className="container">
        {stageArticles.edges.map(({ node }) => (
          <section
            className="chaosEngineering__stage-section"
            key={node.fileAbsolutePath}
          >
            <article className="chaosEngineering__stage-article">
              <Link
                to={node.fields.fullUrl}
                title={`${node.frontmatter.titleSub}: ${node.frontmatter.title}`}
                className="chaosEngineering__stage-article-link"
              />
              <div
                className="chaosEngineering__stage-article-media u-contrast"
                style={{
                  backgroundImage: `url(${node.frontmatter.thumbnailImage})`,
                }}
              />
              <div className="chaosEngineering__stage-article-content">
                <div className="u-mb--large">
                  <h3>{node.frontmatter.title}</h3>
                  <Data markdown>{node.frontmatter.teaser}</Data>
                </div>
                <div className="chaosEngineering__stage-article-bottom">
                  <div className="chaosEngineering__stage-article-cta u-textHeading u-textSpace u-textUppercase u-textPrimary">
                    Read now
                  </div>
                </div>
              </div>
            </article>

            {!isEmpty(node.frontmatter.talkingPoints) && (
              <div className="u-mt--large">
                <h4>What to do in {node.frontmatter.titleSub}</h4>
                <List
                  type={['space', 'number']}
                  className="chaosengineering__talkingPoints"
                >
                  {node.frontmatter.talkingPoints.map(({ point }) => (
                    <ListItem
                      className="u-textHeading u-textNormal u-textFluid--large u-textBlockLevel"
                      key={point}
                    >
                      {point}
                    </ListItem>
                  ))}
                </List>
              </div>
            )}
          </section>
        ))}
      </div>
    </section>
    <section className="section u-bgPanel chaosEngineering__bottomArticles-section">
      <div className="container">
        <div className="section-titleWrapper">
          <h3 className="section-title">
            <Data>{pageData.bottomArticles.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.bottomArticles.blurb}</Data>
          </div>
        </div>
        <ul className="blockGrid blockGrid-2@medium blockGrid--deepMatch">
          <BottomArticles articles={pageData.bottomArticles.articles} />
        </ul>
      </div>
    </section>
  </ChaosEngineeringLayout>
);

ChaosEngineering.propTypes = {
  data: PropTypes.object.isRequired,
};

export default ChaosEngineering;

export const pageQuery = graphql`
  query ChaosEngineeringIndexPageData {
    pageData: file(name: { eq: "chaos-engineering__index" }) {
      childDataYaml {
        title
        heroImage
        socialMediaImage
        description
        hero {
          heading
          blurb
        }
        intro {
          heading
          blurb
          blurbMeta
        }
        what {
          heading
          blurb
        }
        who {
          heading
          blurb
        }
        why {
          heading
          blurb
        }
        bottomArticles {
          heading
          blurb
          articles {
            tutorial {
              ...TutorialTeaserFragment
            }
            post {
              ...PostTeaserFragment
            }
            chaosEngineering {
              ...ChaosEngineeringSingleFragment
            }
          }
        }
        body
      }
    }

    stageArticles: allMarkdownRemark(
      filter: {
        fileAbsolutePath: { regex: "/src/chaos-engineering/" }
        frontmatter: { categories: { eq: "Stage" }, title: { ne: "BLUEPRINT" } }
      }
      sort: { fields: frontmatter___titleSub }
    ) {
      edges {
        node {
          ...ChaosEngineeringSingleFragment
        }
      }
    }
  }
`;
